/**
 * @fileoverview Shopify connect button component with OAuth and Custom App support.
 * @module features/settings/components/ShopifyOAuthConnect
 */

import { type ReactElement, useState, useCallback, useEffect, useRef } from 'react'
import { Plus, Loader2, Store, Eye, EyeOff, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import {
  getShopifyOAuthConfig,
  getShopifyAuthorizationUrl,
  normalizeShopDomain,
  connectShopifyCustomApp,
  type ShopifyConnectionMode,
} from '../api/shopifyOAuthService'

export interface ShopifyOAuthConnectProps {
  /** Callback after connection is successful */
  onConnected?: () => Promise<unknown> | unknown
  /** Custom button label (default: "Connect Store") */
  buttonLabel?: string
  /** Custom button icon element */
  buttonIcon?: React.ReactNode
  /**
   * Connection mode for the store.
   * - DIRECT: Orders synced directly from Shopify API
   * - ATTRIBUTION_ONLY: Only pixel tracking, orders come via OMS
   * Defaults to ATTRIBUTION_ONLY if not specified.
   */
  connectionMode?: ShopifyConnectionMode
  /**
   * Display variant.
   * - button: Small outline button (default)
   * - card: Card entry style matching OmsIntegrationEntry
   */
  variant?: 'button' | 'card'
}

/** Connection method type */
type ConnectionMethod = 'oauth' | 'custom-app'

/** Popup window dimensions */
const POPUP_WIDTH = 600
const POPUP_HEIGHT = 700

/**
 * ShopifyOAuthConnect
 *
 * Button that initiates Shopify connection flow.
 * Supports two methods:
 * - OAuth (App Store): Opens popup for OAuth authorization
 * - Custom App (Token): Direct connection with admin-created access token
 *
 * @component
 */
export function ShopifyOAuthConnect({
  onConnected,
  buttonLabel,
  buttonIcon,
  connectionMode,
  variant = 'button',
}: ShopifyOAuthConnectProps): ReactElement {
  const { t } = useTranslation('features-settings')
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()

  const [showDialog, setShowDialog] = useState(false)
  const [connectionMethod, setConnectionMethod] = useState<ConnectionMethod>('oauth')
  const [shopDomain, setShopDomain] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const popupRef = useRef<Window | null>(null)
  const pollIntervalRef = useRef<number | null>(null)
  const isConnectingRef = useRef(false)

  // Keep ref in sync with state
  useEffect(() => {
    isConnectingRef.current = isConnecting
  }, [isConnecting])

  // Fetch OAuth config to check if configured
  const configQuery = useQuery({
    queryKey: ['shopify', 'oauth', 'config'],
    queryFn: getShopifyOAuthConfig,
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  // Refresh queries after successful connection
  const refreshQueries = useCallback(async () => {
    try {
      await Promise.all([
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey.includes('integrations-status'),
        }),
        queryClient.invalidateQueries({
          queryKey: ['shopify', 'connections'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['stores'],
        }),
      ])
      await onConnected?.()
    } catch (error) {
      console.error('Failed to refresh after Shopify connection:', error)
    }
  }, [onConnected, queryClient])

  // Listen for postMessage from popup and cleanup on unmount
  useEffect(() => {
    const handleMessage = async (event: MessageEvent): Promise<void> => {
      // Verify message type
      if (event.data?.type !== 'shopify-oauth-callback') return

      const { success, message } = event.data as {
        success: boolean
        message: string
        connectionId: string | null
      }

      // Stop polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }

      if (success) {
        toast.success(
          message ||
            t('cards.integrations.shopify.connected', {
              defaultValue: 'Shopify store connected successfully!',
            })
        )
        void refreshQueries()
      } else {
        toast.error(
          message ||
            t('cards.integrations.shopify.error', {
              defaultValue: 'Failed to connect Shopify store',
            })
        )
      }

      // Always cleanup state
      setIsConnecting(false)
      setShowDialog(false)
      resetForm()
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close()
      }
    }
  }, [refreshQueries, t])

  // Poll as fallback - postMessage is primary communication method
  const startPollingForClose = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
    }

    let attempts = 0
    const maxAttempts = 60

    pollIntervalRef.current = window.setInterval(() => {
      attempts++

      let isPopupClosed = false
      try {
        isPopupClosed = !popupRef.current || popupRef.current.closed
      } catch {
        isPopupClosed = true
      }

      if (isPopupClosed) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        setTimeout(() => {
          if (isConnectingRef.current) {
            setIsConnecting(false)
            setShowDialog(false)
            resetForm()
            void queryClient.invalidateQueries({ queryKey: ['stores'] })
            void queryClient.invalidateQueries({ queryKey: ['shopify', 'connections'] })
            void queryClient.invalidateQueries({
              predicate: (query) =>
                Array.isArray(query.queryKey) && query.queryKey.includes('integrations-status'),
            })
          }
        }, 500)
      } else if (attempts >= maxAttempts) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        try {
          popupRef.current?.close()
        } catch {
          // Ignore COOP errors
        }
        toast.error(
          t('cards.integrations.shopify.timeout', {
            defaultValue: 'Authorization timed out. Please try again.',
          })
        )
        setIsConnecting(false)
      }
    }, 2000)
  }, [t, queryClient])

  const resetForm = (): void => {
    setShopDomain('')
    setClientId('')
    setClientSecret('')
    setShowSecret(false)
  }

  // Handle OAuth connection
  const handleOAuthConnect = useCallback(async () => {
    if (!configQuery.data?.configured) {
      toast.error(
        t('cards.integrations.shopify.notConfigured', {
          defaultValue: 'Shopify OAuth is not configured. Please contact support.',
        })
      )
      return
    }

    setIsConnecting(true)

    try {
      const normalizedDomain = normalizeShopDomain(shopDomain)
      const { authorizationUrl } = await getShopifyAuthorizationUrl({
        shopDomain: normalizedDomain,
        ...(connectionMode && { connectionMode }),
      })

      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2

      popupRef.current = window.open(
        authorizationUrl,
        'shopify-oauth',
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`
      )

      if (!popupRef.current) {
        toast.error(
          t('cards.integrations.shopify.popupBlocked', {
            defaultValue: 'Popup was blocked. Please allow popups for this site.',
          })
        )
        setIsConnecting(false)
        return
      }

      startPollingForClose()
    } catch (error) {
      toast.error(
        (error as Error).message ||
          t('cards.integrations.shopify.error', {
            defaultValue: 'Failed to start Shopify authorization',
          })
      )
      setIsConnecting(false)
    }
  }, [configQuery.data?.configured, connectionMode, shopDomain, startPollingForClose, t])

  // Handle Custom App connection (Client Credentials Grant)
  const handleCustomAppConnect = useCallback(async () => {
    setIsConnecting(true)

    try {
      const normalizedDomain = normalizeShopDomain(shopDomain)

      await connectShopifyCustomApp({
        shopDomain: normalizedDomain,
        clientId,
        clientSecret,
        connectionMode: connectionMode ?? 'ATTRIBUTION_ONLY',
      })

      toast.success(
        t('cards.integrations.shopify.connected', {
          defaultValue: 'Shopify store connected successfully!',
        })
      )

      await refreshQueries()

      setIsConnecting(false)
      setShowDialog(false)
      resetForm()
    } catch (error) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message
      toast.error(
        errorMessage ||
          t('cards.integrations.shopify.error', {
            defaultValue: 'Failed to connect Shopify store',
          })
      )
      setIsConnecting(false)
    }
  }, [clientId, clientSecret, connectionMode, refreshQueries, shopDomain, t])

  // Main connect handler
  const handleConnect = useCallback(async () => {
    if (!shopDomain.trim()) {
      toast.error(
        t('cards.integrations.shopify.shopRequired', {
          defaultValue: 'Please enter your Shopify store domain',
        })
      )
      return
    }

    if (connectionMethod === 'custom-app') {
      if (!clientId.trim()) {
        toast.error(
          t('cards.integrations.shopify.clientIdRequired', {
            defaultValue: 'Please enter your Client ID',
          })
        )
        return
      }
      if (!clientSecret.trim()) {
        toast.error(
          t('cards.integrations.shopify.clientSecretRequired', {
            defaultValue: 'Please enter your Client Secret',
          })
        )
        return
      }
    }

    if (connectionMethod === 'oauth') {
      await handleOAuthConnect()
    } else {
      await handleCustomAppConnect()
    }
  }, [connectionMethod, shopDomain, clientId, clientSecret, handleOAuthConnect, handleCustomAppConnect, t])

  const handleOpenDialog = (): void => {
    // For OAuth, check if configured
    if (connectionMethod === 'oauth' && !configQuery.data?.configured) {
      // Switch to custom-app if OAuth not configured
      setConnectionMethod('custom-app')
    }
    setShowDialog(true)
  }

  const handleCloseDialog = (): void => {
    setShowDialog(false)
    resetForm()
  }

  const isLoading = configQuery.isLoading || isConnecting
  const isDisabled = !activeOrgId || isLoading
  const isOAuthConfigured = configQuery.data?.configured ?? false

  // Can connect if: OAuth mode and configured, OR custom-app mode with credentials
  const canConnect =
    shopDomain.trim() &&
    !isConnecting &&
    (connectionMethod === 'oauth'
      ? isOAuthConfigured
      : clientId.trim() && clientSecret.trim())

  const label =
    buttonLabel ??
    t('cards.integrations.shopify.buttons.connectStore', {
      defaultValue: 'Connect Store',
    })

  return (
    <>
      {variant === 'card' ? (
        <button
          type="button"
          onClick={handleOpenDialog}
          disabled={isDisabled}
          className="flex flex-col rounded-lg border border-border bg-card p-4 cursor-pointer transition-[filter] hover:brightness-105 disabled:opacity-50 disabled:pointer-events-none w-full text-left"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                {isLoading ? (
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                ) : (
                  <img src="/shopify.webp" alt="Shopify" className="size-5" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground truncate">Shopify</span>
                </div>
                <span className="text-sm text-muted-foreground">{label}</span>
              </div>
            </div>
            <Plus className="size-4 text-muted-foreground" />
          </div>
        </button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleOpenDialog}
          disabled={isDisabled}
          className="gap-2"
        >
          {isLoading ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            buttonIcon ?? <Plus className="size-3.5" />
          )}
          {label}
        </Button>
      )}

      {/* Connection Dialog */}
      <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="size-5" />
              {t('cards.integrations.shopify.dialog.title', {
                defaultValue: 'Connect Shopify Store',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('cards.integrations.shopify.dialog.description', {
                defaultValue:
                  'Enter your Shopify store domain to connect it for attribution tracking.',
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {/* Connection Method Toggle */}
            <div className="space-y-2">
              <Label>
                {t('cards.integrations.shopify.dialog.connectionMethod', {
                  defaultValue: 'Connection Method',
                })}
              </Label>
              <ToggleGroup
                type="single"
                value={connectionMethod}
                onValueChange={(value) => value && setConnectionMethod(value as ConnectionMethod)}
                variant="outline"
                className="w-full"
              >
                <ToggleGroupItem value="oauth" className="flex-1">
                  {t('cards.integrations.shopify.dialog.methods.oauth', {
                    defaultValue: 'App Store',
                  })}
                </ToggleGroupItem>
                <ToggleGroupItem value="custom-app" className="flex-1">
                  {t('cards.integrations.shopify.dialog.methods.customApp', {
                    defaultValue: 'Custom App',
                  })}
                </ToggleGroupItem>
              </ToggleGroup>
              <p className="text-xs text-muted-foreground">
                {connectionMethod === 'oauth'
                  ? t('cards.integrations.shopify.dialog.methods.oauthHint', {
                      defaultValue: 'Recommended. Install via Shopify App Store.',
                    })
                  : t('cards.integrations.shopify.dialog.methods.customAppHint', {
                      defaultValue: 'Use your own Custom App token from Shopify Admin.',
                    })}
              </p>
            </div>

            {/* Store Domain Input */}
            <div className="space-y-2">
              <Label htmlFor="shop-domain">
                {t('cards.integrations.shopify.dialog.shopDomain', {
                  defaultValue: 'Store Domain',
                })}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="shop-domain"
                  value={shopDomain}
                  onChange={(e) => setShopDomain(e.target.value)}
                  placeholder="mystore"
                  disabled={isConnecting}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canConnect) {
                      handleConnect()
                    }
                  }}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  .myshopify.com
                </span>
              </div>
            </div>

            {/* Client Credentials Inputs (Custom App only) */}
            {connectionMethod === 'custom-app' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="client-id">
                    {t('cards.integrations.shopify.dialog.clientId', {
                      defaultValue: 'Client ID',
                    })}
                  </Label>
                  <Input
                    id="client-id"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="aee4661767a0636f8f3eda3e6a88e4f4"
                    disabled={isConnecting}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canConnect) {
                        handleConnect()
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-secret">
                    {t('cards.integrations.shopify.dialog.clientSecret', {
                      defaultValue: 'Client Secret',
                    })}
                  </Label>
                  <div className="relative">
                    <Input
                      id="client-secret"
                      type={showSecret ? 'text' : 'password'}
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder="shpss_xxxxxxxxxxxxx"
                      disabled={isConnecting}
                      className="pr-10"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && canConnect) {
                          handleConnect()
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <a
                      href="https://shopify.dev/docs/apps/build/authentication-authorization/access-tokens/client-credentials-grant"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {t('cards.integrations.shopify.dialog.credentialsHelp', {
                        defaultValue: 'How to get credentials from Dev Dashboard',
                      })}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </>
            )}

            {/* OAuth not configured warning */}
            {connectionMethod === 'oauth' && !isOAuthConfigured && !configQuery.isLoading && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {t('cards.integrations.shopify.dialog.oauthNotConfigured', {
                    defaultValue:
                      'App Store connection is not available. Please use Custom App instead.',
                  })}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                disabled={isConnecting}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="button" onClick={handleConnect} disabled={!canConnect}>
                {isConnecting ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    {t('cards.integrations.shopify.buttons.connecting', {
                      defaultValue: 'Connecting...',
                    })}
                  </>
                ) : (
                  t('cards.integrations.shopify.buttons.connect', { defaultValue: 'Connect' })
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ShopifyOAuthConnect
